import setupRouter from '@/app/router';
import { kinesisService } from '@/shared/services/kinesis';
import { amplitudeService } from '@/shared/services/amplitude';
import { validateInitialState } from '@/shared/api/initial-state-validation';

import MainApp from './App.vue';

if (import.meta.env.DEV) {
  import('@/shared/assets/scss/index.scss');
}

(async () => {
  // Dev only: a module's fixtures.ts replaces the index.html mock state
  if (import.meta.env.DEV) {
    const { applyFixture } = await import('@/app/dev/fixtures');
    applyFixture();
  }

  // Report only: the page renders as before, Sentry gets the problems below.
  const initialStateProblems = validateInitialState(window.initialState);

  if (import.meta.env.DEV && initialStateProblems.length) {
    // eslint-disable-next-line no-console
    console.warn('[initialState]', initialStateProblems);
  }

  const pinia = createPinia();
  const app = createApp(MainApp);

  const router = await setupRouter(app);

  const needI18n = router.currentRoute.value.meta.i18nAlias;

  if (needI18n) {
    await import('@/app/locales/i18n').then((m) =>
      m.setupI18n({
        vueApp: app,
        pageName: router.currentRoute.value.meta.i18nAlias as string,
        router,
      }),
    );
  }

  app.use(pinia);
  app.mount('#app');

  kinesisService.init();
  amplitudeService.init();

  // Load Sentry AFTER mount — doesn't block rendering
  if (import.meta.env.VITE_SENTRY_DSN) {
    const {
      init,
      captureMessage,
      browserTracingIntegration,
      browserSessionIntegration,
      replayIntegration,
    } = await import('@sentry/vue');

    // Sentry v11 collects user info, cookies, headers and HTTP bodies by
    // default. Keep the v10 (sendDefaultPii: false) behavior on checkout.
    const piiDenyList = {
      deny: ['forwarded', '-ip', 'remote-', 'via', '-user'],
    };

    // Query params that identify the user or the ad click: the header names above match none
    // of them, and Sentry itself filters only auth / token / session / key-like names.
    const queryParamsDenyList = {
      deny: [
        'udid',
        'mp_user_id',
        'user_id',
        'email',
        'redeem_code',
        'gclid',
        'fbclid',
        'twclid',
      ],
    };

    init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: piiDenyList,
        httpBodies: [],
        urlQueryParams: queryParamsDenyList,
      },
      // v10 behavior: transactions instead of span streaming, no stack trace
      // on captureMessage, a new session per route change
      traceLifecycle: 'static',
      attachStacktrace: false,
      integrations: [
        browserTracingIntegration({ router }),
        browserSessionIntegration({ lifecycle: 'route' }),
        replayIntegration({ maskAllText: true, blockAllMedia: true }),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      ignoreErrors: [
        'URI malformed',
        'Failed to load the Google 3P Authorization JavaScript Library.',
      ],
    });

    if (initialStateProblems.length) {
      captureMessage('initialState does not match the frontend types', {
        level: 'warning',
        extra: { problems: initialStateProblems.slice(0, 50) },
      });
    }
  }
})();
